import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import {
  Plus, Trash, Clipboard, X as XIcon, Package, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

const fmtDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
};
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("it-IT");
};

export default function InventoryCounts() {
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [lastByProduct, setLastByProduct] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [w, p, li, list] = await Promise.all([
        api.get("/warehouses"),
        api.get("/products"),
        api.get("/inventory-counts/products/last-inventory"),
        api.get("/inventory-counts?limit=200"),
      ]);
      setWarehouses((w.data || []).filter((x) => x.type === "physical"));
      setProducts((p.data || []).filter((x) => x.is_serialized === false));
      setLastByProduct(li.data || {});
      setRows(list.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Controllo giacenze"
        title="Inventario"
        subtitle="Conta fisica dei materiali bulk: il sistema allinea la giacenza ai valori rilevati."
        action={
          <button
            data-testid="inv-count-new-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white hover:bg-[#002480] transition-colors text-sm"
          >
            <Plus size={16} weight="bold" /> Nuovo inventario
          </button>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="inv-count-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Data</th>
                <th className="px-4 py-3 label-eyebrow">Magazzino</th>
                <th className="px-4 py-3 label-eyebrow">Prodotti</th>
                <th className="px-4 py-3 label-eyebrow text-right">Differenze</th>
                <th className="px-4 py-3 label-eyebrow">Autore</th>
                <th className="px-4 py-3 label-eyebrow">Nota</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-neutral-500" data-testid="inv-count-empty">
                  {loading ? "Caricamento…" : "Nessun inventario registrato."}
                </td></tr>
              ) : rows.map((r) => {
                const diffs = (r.items || []).filter((it) => it.delta !== 0).length;
                return (
                  <tr
                    key={r.id}
                    data-testid={`inv-count-row-${r.id}`}
                    onClick={() => setDetail(r)}
                    className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{fmtDateTime(r.created_at)}</td>
                    <td className="px-4 py-3">{r.warehouse_name}</td>
                    <td className="px-4 py-3">{(r.items || []).length}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {diffs === 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={14} weight="fill" /> Nessuna</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700"><WarningCircle size={14} weight="fill" /> {diffs}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-600">{r.created_by_name || r.created_by || "—"}</td>
                    <td className="px-4 py-3 text-xs text-neutral-700 max-w-xs truncate">{r.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <InventoryCountModal
          warehouses={warehouses}
          products={products}
          lastByProduct={lastByProduct}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {detail && <InventoryCountDetail data={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function InventoryCountModal({ warehouses, products, lastByProduct, onClose, onSaved }) {
  const [warehouseId, setWarehouseId] = useState("");
  const [items, setItems] = useState([{ product_id: "", counted_qty: "", tracked_qty: null }]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const warehouseOpts = useMemo(
    () => warehouses.map((w) => ({ value: w.id, label: w.name, keywords: [w.name] })),
    [warehouses],
  );

  const productOpts = useMemo(() => products.map((p) => {
    const last = lastByProduct[p.id]?.at;
    return {
      value: p.id,
      label: `${p.sku} · ${p.name}`,
      sublabel: last ? `Ultimo inventario: ${fmtDate(last)}` : "Mai inventariato",
      keywords: [p.sku, p.name, p.manufacturer_code].filter(Boolean),
    };
  }), [products, lastByProduct]);

  const fetchTracked = async (idx, productId) => {
    if (!warehouseId || !productId) return;
    try {
      const { data } = await api.get(`/inventory/by-product/${productId}`);
      const wh = (data || []).find((r) => r.warehouse_id === warehouseId);
      setItems((prev) => prev.map((it, i) => i === idx ? { ...it, tracked_qty: wh ? wh.quantity : 0 } : it));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
    if (patch.product_id) fetchTracked(idx, patch.product_id);
  };
  const removeItem = (idx) => setItems((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));
  const addItem = () => setItems((prev) => [...prev, { product_id: "", counted_qty: "", tracked_qty: null }]);

  useEffect(() => {
    // Refresh tracked quantities when the warehouse changes.
    items.forEach((it, i) => { if (it.product_id) fetchTracked(i, it.product_id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  const submit = async () => {
    if (!warehouseId) return toast.error("Seleziona il magazzino");
    if (!note.trim() || note.trim().length < 3) return toast.error("La nota è obbligatoria (min. 3 caratteri)");
    const cleaned = items
      .map((it) => ({ product_id: it.product_id, counted_qty: parseInt(it.counted_qty, 10) }))
      .filter((it) => it.product_id && Number.isFinite(it.counted_qty) && it.counted_qty >= 0);
    if (!cleaned.length) return toast.error("Aggiungi almeno un prodotto con una quantità rilevata");
    const seen = new Set();
    for (const it of cleaned) {
      if (seen.has(it.product_id)) return toast.error("Stesso prodotto indicato più volte");
      seen.add(it.product_id);
    }
    setSaving(true);
    try {
      await api.post("/inventory-counts", { warehouse_id: warehouseId, note: note.trim(), items: cleaned });
      toast.success("Inventario registrato");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const disabled = saving || !warehouseId || !note.trim() || items.every((it) => !it.product_id);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-white w-full max-w-4xl my-6 border border-neutral-300 shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="inv-count-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <div className="font-display font-bold tracking-tight flex items-center gap-2">
            <Clipboard size={18} weight="bold" className="text-[#002FA7]" /> Nuovo inventario
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-200"><XIcon size={16} /></button>
        </div>
        <div className="p-4 md:p-6 space-y-4">
          <div>
            <label className="label-eyebrow text-neutral-700">Magazzino *</label>
            <SearchableSelect
              testid="inv-count-warehouse"
              value={warehouseId}
              options={warehouseOpts}
              onChange={setWarehouseId}
              placeholder="Seleziona il magazzino fisico…"
            />
            {!warehouses.length && (
              <p className="text-xs text-amber-700 mt-1">Nessun magazzino fisico disponibile.</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow text-neutral-700">Prodotti inventariati</span>
              <button
                type="button"
                data-testid="inv-count-add-item"
                onClick={addItem}
                className="text-xs inline-flex items-center gap-1 text-[#002FA7] hover:underline"
              >
                <Plus size={12} weight="bold" /> Aggiungi prodotto
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start p-2 border border-neutral-200 bg-neutral-50/50" data-testid={`inv-count-item-${idx}`}>
                  <div className="col-span-12 md:col-span-6">
                    <span className="label-eyebrow block mb-1">Prodotto</span>
                    <SearchableSelect
                      testid={`inv-count-product-${idx}`}
                      value={it.product_id}
                      options={productOpts}
                      onChange={(v) => updateItem(idx, { product_id: v })}
                      placeholder="Seleziona prodotto bulk…"
                    />
                    {it.product_id && (
                      <div className="text-[11px] text-neutral-500 mt-1" data-testid={`inv-count-last-${idx}`}>
                        {lastByProduct[it.product_id]?.at
                          ? `Ultimo inventario: ${fmtDate(lastByProduct[it.product_id].at)}`
                          : "Mai inventariato"}
                      </div>
                    )}
                  </div>
                  <div className="col-span-5 md:col-span-2">
                    <span className="label-eyebrow block mb-1">A sistema</span>
                    <div className="h-[38px] px-3 py-2 border border-neutral-200 bg-white text-sm flex items-center justify-end font-mono text-neutral-700"
                         data-testid={`inv-count-tracked-${idx}`}>
                      {it.tracked_qty === null ? "—" : it.tracked_qty}
                    </div>
                  </div>
                  <div className="col-span-5 md:col-span-3">
                    <span className="label-eyebrow block mb-1">Rilevato *</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={it.counted_qty}
                      onChange={(e) => updateItem(idx, { counted_qty: e.target.value })}
                      className="w-full h-[38px] px-3 py-2 border border-neutral-300 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      data-testid={`inv-count-counted-${idx}`}
                      placeholder="0"
                    />
                    {it.tracked_qty !== null && it.counted_qty !== "" && (
                      <div
                        data-testid={`inv-count-delta-${idx}`}
                        className={`text-[11px] mt-1 font-mono text-right ${(+it.counted_qty - it.tracked_qty) === 0 ? "text-emerald-700" : "text-amber-700"}`}
                      >
                        {(+it.counted_qty - it.tracked_qty) > 0 ? "+" : ""}{+it.counted_qty - it.tracked_qty} vs sistema
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 md:col-span-1 flex items-end justify-end">
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      className="h-[38px] w-full border border-neutral-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 flex items-center justify-center transition-colors"
                      data-testid={`inv-count-remove-${idx}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow text-neutral-700">Motivo dell'inventario *</label>
            <textarea
              data-testid="inv-count-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required
              rows={3}
              placeholder="Perché si è reso necessario l'inventario? (obbligatorio)"
              className="w-full mt-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>

          <div className="flex gap-2 pt-2 border-t border-neutral-200">
            <button
              type="button"
              data-testid="inv-count-submit"
              onClick={submit}
              disabled={disabled}
              className="px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-50 text-sm"
            >
              {saving ? "Salvataggio…" : "Registra inventario"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 text-sm hover:bg-neutral-50"
            >Annulla</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InventoryCountDetail({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-white w-full max-w-3xl my-6 border border-neutral-300 shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="inv-count-detail">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <div className="font-display font-bold tracking-tight">
            Inventario · {data.warehouse_name}
            <span className="ml-2 text-xs text-neutral-500 font-normal">{fmtDateTime(data.created_at)}</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-200"><XIcon size={16} /></button>
        </div>
        <div className="p-4 md:p-6 space-y-3">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="px-2 py-1.5 text-left">SKU</th>
                <th className="px-2 py-1.5 text-left">Prodotto</th>
                <th className="px-2 py-1.5 text-right">A sistema</th>
                <th className="px-2 py-1.5 text-right">Rilevato</th>
                <th className="px-2 py-1.5 text-right">Differenza</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((it, i) => (
                <tr key={i} className="border-b border-neutral-100 last:border-b-0">
                  <td className="px-2 py-1.5 font-mono">{it.product_sku}</td>
                  <td className="px-2 py-1.5"><Package size={12} className="inline text-neutral-400 mr-1" />{it.product_name}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{it.tracked_qty} <span className="text-neutral-400 text-xs">{it.unit_of_measure}</span></td>
                  <td className="px-2 py-1.5 text-right font-mono">{it.counted_qty}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${it.delta === 0 ? "text-emerald-700" : "text-amber-700"}`}>{it.delta > 0 ? "+" : ""}{it.delta}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <div className="label-eyebrow mb-1">Motivo</div>
            <div className="text-sm whitespace-pre-wrap">{data.note}</div>
          </div>
          <div className="text-xs text-neutral-500">
            Registrato da: {data.created_by_name || data.created_by || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}
